<!-- /**
 * 이 컴포넌트는 MeetSync 웹사이트의 랜딩 페이지 역할을 하며,
 * Hero, 이해관계자, 주요 기능, CTA(콜 투 액션) 섹션으로 구성된 스냅 기반 수직 스크롤 인터페이스를 제공합니다.
 * 각 섹션은 전체 화면을 차지하며, 사용자의 스크롤/터치/키보드 입력에 따라 부드럽게 다음 섹션으로 전환됩니다.
 * 사용자 경험을 극대화하기 위해 debounce 및 가시성 상태 추적 로직도 포함되어 있습니다.
 */ -->

<template>
  <main class="home-container" ref="mainContainer">
    <HeroSection class="snap-section" />
    <!-- 🔧 UI/UX 개선: 3개 섹션을 하나의 공통 배경 위에서 스크롤로 전환 -->
    <div class="sections-with-background">
      <AnimatedBackground />
      <StakeHoldersSection class="snap-section" />
      <MainFunctionsSection class="snap-section" />
      <CTASection class="snap-section" />
    </div>
  </main>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import HeroSection from '@/components/HeroSection.vue'
import StakeHoldersSection from '@/components/StakeHoldersSection.vue'
import MainFunctionsSection from '@/components/MainFunctionsSection.vue'
import CTASection from '@/components/CTASection.vue'
// 🔧 UI/UX 개선: 애니메이션 배경 컴포넌트 import
import AnimatedBackground from '@/components/AnimatedBackground.vue'

// DOM 참조 및 상태 선언
const mainContainer = ref(null)
const sections = ref([])
const currentSectionIndex = ref(0)
const isScrolling = ref(false)
const isVisible = ref([false, false, false, false])

let lastScrollTime = 0
const scrollThreshold = 500 // 최소 스크롤 간격 (ms)

// 지정된 인덱스의 섹션으로 스크롤
const scrollToSection = (index) => {
  if (isScrolling.value || index < 0 || index >= sections.value.length) return

  isScrolling.value = true
  sections.value[index].scrollIntoView({ behavior: 'smooth' })
  currentSectionIndex.value = index

  // 스크롤 완료 후 상태 업데이트
  setTimeout(() => {
    isScrolling.value = false
    isVisible.value[index] = true
  }, 1000)
}

// 현재 보여지는 섹션 계산
const updateCurrentSection = () => {
  if (!mainContainer.value) return

  const viewportHeight = window.innerHeight
  let newIndex = 0

  sections.value.forEach((section, index) => {
    const rect = section.getBoundingClientRect()
    if (rect.top <= viewportHeight / 2 && rect.bottom >= viewportHeight / 2) {
      newIndex = index
    }
  })

  currentSectionIndex.value = newIndex
  isVisible.value[newIndex] = true
}

onMounted(() => {
  sections.value = Array.from(document.querySelectorAll('.snap-section'))
  updateCurrentSection()

  // 마우스 휠 입력 처리
  const handleWheel = (event) => {
    event.preventDefault()
    const now = Date.now()
    if (isScrolling.value || now - lastScrollTime < scrollThreshold) return
    const direction = event.deltaY > 0 ? 1 : -1
    scrollToSection(currentSectionIndex.value + direction)
    lastScrollTime = now
  }

  // 터치 제스처 처리
  let touchStartY = 0
  let touchEndY = 0
  let lastTouchTime = 0

  const handleTouchStart = (event) => {
    touchStartY = event.touches[0].clientY
    touchEndY = touchStartY
  }

  const handleTouchMove = (event) => {
    touchEndY = event.touches[0].clientY
  }

  const handleTouchEnd = () => {
    const now = Date.now()
    if (isScrolling.value || now - lastTouchTime < scrollThreshold) return
    const touchDiff = touchStartY - touchEndY
    if (Math.abs(touchDiff) > 50) {
      const direction = touchDiff > 0 ? 1 : -1
      scrollToSection(currentSectionIndex.value + direction)
      lastTouchTime = now
    }
  }

  // 키보드 입력 처리
  const handleKeyDown = (event) => {
    const now = Date.now()
    if (isScrolling.value || now - lastScrollTime < scrollThreshold) return
    if (['ArrowDown', 'PageDown'].includes(event.key)) {
      event.preventDefault()
      scrollToSection(currentSectionIndex.value + 1)
      lastScrollTime = now
    } else if (['ArrowUp', 'PageUp'].includes(event.key)) {
      event.preventDefault()
      scrollToSection(currentSectionIndex.value - 1)
      lastScrollTime = now
    }
  }

  // 스크롤 완료 후 현재 섹션 재계산
  const handleScrollEnd = () => {
    updateCurrentSection()
  }

  // 이벤트 등록
  window.addEventListener('wheel', handleWheel, { passive: false })
  window.addEventListener('touchstart', handleTouchStart)
  window.addEventListener('touchmove', handleTouchMove)
  window.addEventListener('touchend', handleTouchEnd)
  window.addEventListener('keydown', handleKeyDown)
  window.addEventListener('scroll', handleScrollEnd, { passive: true })

  // 언마운트 시 이벤트 해제
  onBeforeUnmount(() => {
    window.removeEventListener('wheel', handleWheel)
    window.removeEventListener('touchstart', handleTouchStart)
    window.removeEventListener('touchmove', handleTouchMove)
    window.removeEventListener('touchend', handleTouchEnd)
    window.removeEventListener('keydown', handleKeyDown)
    window.removeEventListener('scroll', handleScrollEnd)
  })
})
</script>

<style scoped>
.home-container {
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  scroll-snap-type: y mandatory;
  margin: 0;
  padding: 0;
  position: relative;
  /* 🔧 UI/UX 개선: 그라데이션 배경 추가 */
  background: linear-gradient(135deg, #f0f9ff 0%, #ffffff 50%, #f0f9ff 100%);
}

.sections-with-background {
  position: relative;
  width: 100%;
  height: 300vh; /* 3개 섹션의 높이 */
}

.snap-section {
  scroll-snap-align: start;
  scroll-snap-stop: always;
  width: 100%;
  height: 100vh;
  margin: 0;
  padding: 0;
  position: relative;
  z-index: 1;
}
</style>